import type { DetectionResult } from "../vision";
import { isDoubleBlank, scoreDomino, scoreTotal } from "../lib/scoring";

export interface DominoResultsProps {
  result: DetectionResult;
  /** When true, a double-blank (0,0) domino is scored as 50 points. */
  doubleBlankIs50: boolean;
}

/**
 * Presentational component that lists every detected domino by its two-half
 * pip values and shows the group total. Pure render — no side effects.
 */
export function DominoResults({ result, doubleBlankIs50 }: DominoResultsProps) {
  const { dominoes } = result;
  const options = { doubleBlankIs50 };
  const total = scoreTotal(dominoes, options);

  if (dominoes.length === 0) {
    return (
      <div className="results results--empty" role="status">
        <p>No dominoes were detected.</p>
        <p className="results__hint">
          Try a clearer, top-down photo of light dominoes on a darker, contrasting
          surface.
        </p>
      </div>
    );
  }

  return (
    <div className="results" role="status">
      <h2 className="results__heading">
        {dominoes.length} domino{dominoes.length === 1 ? "" : "es"} detected
      </h2>
      <ul className="results__list" aria-label="Detected dominoes">
        {dominoes.map((domino, i) => {
          const scored = doubleBlankIs50 && isDoubleBlank(domino);
          return (
            <li
              className={`results__item${scored ? " results__item--bonus" : ""}`}
              key={i}
              data-testid="domino-item"
              title={scored ? "Double-blank scored as 50" : undefined}
            >
              <span className="results__values">
                {domino.values[0]},{domino.values[1]}
              </span>
              <span className="results__subtotal">
                = {scoreDomino(domino, options)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="results__total" data-testid="total-pips">
        Total: <strong>{total}</strong>
      </p>
    </div>
  );
}
